/**
 * Cloudinary Media Storage Integration for ASMM Portal
 * Secure, permanent cloud asset storage with organized directory hierarchy.
 */

export const CLOUDINARY_CONFIG = {
  cloudName: (import.meta.env.VITE_CLOUDINARY_CLOUD_NAME as string) || 'dntsgcacv',
  apiKey: (import.meta.env.VITE_CLOUDINARY_API_KEY as string) || '942223439676373',
  apiSecret: (import.meta.env.VITE_CLOUDINARY_API_SECRET as string) || 'Q8Kzw2HJvWeXYg6bH6dZzcvLaQU',
  rootFolder: (import.meta.env.VITE_CLOUDINARY_FOLDER as string) || 'asmm',
};

// Organized directory hierarchy inside Cloudinary 'asmm'
export const CLOUDINARY_FOLDERS = {
  ROOT: CLOUDINARY_CONFIG.rootFolder,
  MERCHANDISE: `${CLOUDINARY_CONFIG.rootFolder}/merchandise`,
  MEMBERS: `${CLOUDINARY_CONFIG.rootFolder}/members`,
  RECEIPTS: `${CLOUDINARY_CONFIG.rootFolder}/receipts`,
  DOCUMENTS: `${CLOUDINARY_CONFIG.rootFolder}/documents`,
  CAMPAIGNS: `${CLOUDINARY_CONFIG.rootFolder}/campaigns`,
} as const;

export interface CloudinaryUploadResponse {
  asset_id: string;
  public_id: string;
  version: number;
  width?: number;
  height?: number;
  format: string;
  resource_type: string;
  bytes: number;
  url: string;
  secure_url: string;
  created_at: string;
}

export interface CloudinaryUploadOptions {
  folder?: string;
  publicId?: string;
  resourceType?: 'image' | 'raw' | 'auto';
  tags?: string[];
}

/**
 * Computes SHA-1 hash using browser native Web Crypto API
 */
async function generateSha1(str: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(str);
  const hashBuffer = await crypto.subtle.digest('SHA-1', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Direct signed upload to Cloudinary via HTTPS
 * Returns permanent secure CDN HTTPS URL (e.g. https://res.cloudinary.com/dntsgcacv/image/upload/...)
 */
export async function uploadToCloudinary(
  file: File | Blob | string,
  options: CloudinaryUploadOptions = {}
): Promise<CloudinaryUploadResponse> {
  const {
    folder = CLOUDINARY_FOLDERS.MERCHANDISE,
    publicId,
    resourceType = 'auto'
  } = options;

  const timestamp = Math.floor(Date.now() / 1000);

  // Cloudinary signature rules:
  // Sort parameters alphabetically, join with '=', join pairs with '&', append apiSecret
  const signParams: Record<string, string> = {
    folder,
    timestamp: String(timestamp),
  };

  if (publicId) {
    signParams.public_id = publicId;
  }

  const sortedKeys = Object.keys(signParams).sort();
  const stringToSign = sortedKeys.map(k => `${k}=${signParams[k]}`).join('&') + CLOUDINARY_CONFIG.apiSecret;
  const signature = await generateSha1(stringToSign);

  const formData = new FormData();
  formData.append('file', file);
  formData.append('api_key', CLOUDINARY_CONFIG.apiKey);
  formData.append('timestamp', String(timestamp));
  formData.append('folder', folder);
  formData.append('signature', signature);

  if (publicId) {
    formData.append('public_id', publicId);
  }

  const endpoint = `https://api.cloudinary.com/v1_1/${CLOUDINARY_CONFIG.cloudName}/${resourceType}/upload`;

  const res = await fetch(endpoint, {
    method: 'POST',
    body: formData,
  });

  if (!res.ok) {
    let errorDetail = 'Upload failed';
    try {
      const errJson = await res.json();
      errorDetail = errJson.error?.message || JSON.stringify(errJson);
    } catch (_) {
      errorDetail = `HTTP ${res.status}: ${res.statusText}`;
    }
    throw new Error(`Cloudinary Upload Error: ${errorDetail}`);
  }

  const data: CloudinaryUploadResponse = await res.json();
  return data;
}

/**
 * Specialized helper: Upload merchandise product image
 * Stored in: asmm/merchandise/
 */
export async function uploadProductImageToCloudinary(
  file: File,
  productId?: string,
  productName?: string
): Promise<{ secureUrl: string; publicId: string; bytes: number }> {
  const cleanId = (productId || 'prod').replace(/[^a-zA-Z0-9_-]/g, '');
  const cleanName = (productName || 'item').trim().toLowerCase().replace(/\s+/g, '-').replace(/[^a-zA-Z0-9_-]/g, '');
  const publicId = `${cleanId}-${cleanName}-${Date.now()}`.slice(0, 80);

  const result = await uploadToCloudinary(file, {
    folder: CLOUDINARY_FOLDERS.MERCHANDISE,
    publicId,
    resourceType: 'image'
  });

  return {
    secureUrl: result.secure_url,
    publicId: result.public_id,
    bytes: result.bytes
  };
}

/**
 * Specialized helper: Upload member profile photo
 * Stored in: asmm/members/<icNo>/
 */
export async function uploadMemberPhotoToCloudinary(
  file: File,
  icNo: string,
  memberName?: string
): Promise<{ secureUrl: string; publicId: string }> {
  const cleanIc = (icNo || 'general').replace(/[^0-9a-zA-Z_]/g, '');
  const cleanName = (memberName || 'photo').trim().toLowerCase().replace(/\s+/g, '-').replace(/[^a-zA-Z0-9_-]/g, '');
  const folder = `${CLOUDINARY_FOLDERS.MEMBERS}/${cleanIc}`;
  const publicId = `profile-${cleanName}-${Date.now()}`.slice(0, 80);

  const result = await uploadToCloudinary(file, {
    folder,
    publicId,
    resourceType: 'image'
  });

  return {
    secureUrl: result.secure_url,
    publicId: result.public_id
  };
}

/**
 * Resolves any Cloudinary asset URL or stored public_id into a fully-qualified, 
 * reliable HTTPS display URL.
 * 
 * Handles:
 * 1. Public IDs (e.g. "asmm/receipts/receipt-INV-202610-110-1793067047365" or "receipt-INV-...")
 * 2. Cloudinary PDF delivery URLs (converts .pdf to .jpg to avoid 401 Unauthorized restrictions)
 * 3. Base64 data URLs & blob URLs (passed through unchanged)
 * 4. External non-Cloudinary HTTP URLs (passed through unchanged)
 */
export function resolveCloudinaryUrl(urlOrPublicId: string | null | undefined): string {
  if (!urlOrPublicId) return '';
  const raw = String(urlOrPublicId).trim();
  if (!raw) return '';

  // 1. Data URLs & Blob URLs
  if (raw.startsWith('data:') || raw.startsWith('blob:')) {
    return raw;
  }

  // 2. Full HTTP/HTTPS URLs
  if (raw.startsWith('http://') || raw.startsWith('https://')) {
    // If it's a Cloudinary URL
    if (raw.includes('cloudinary.com')) {
      // Cloudinary restricts direct raw .pdf downloads on standard tiers (returns 401 Unauthorized image/gif).
      // Replacing .pdf with .jpg causes Cloudinary's dynamic transformation engine
      // to render the first page as a high-quality raster image with 200 OK.
      if (raw.toLowerCase().endsWith('.pdf')) {
        return raw.replace(/\.pdf$/i, '.jpg');
      }
      if (raw.includes('.pdf?')) {
        return raw.replace(/\.pdf\?/i, '.jpg?');
      }
    }
    return raw;
  }

  // 3. Stored as public_id (e.g. "asmm/receipts/receipt-..." or "receipt-...")
  // Clean off any accidental leading slashes or .pdf extension
  let cleanId = raw.replace(/^\/+/, '');
  const isPdfExt = cleanId.toLowerCase().endsWith('.pdf');
  if (isPdfExt) {
    cleanId = cleanId.slice(0, -4);
  }

  // If missing root prefix, check if it starts with 'receipt' or similar
  if (!cleanId.startsWith(CLOUDINARY_CONFIG.rootFolder + '/')) {
    if (cleanId.startsWith('receipt-') || cleanId.startsWith('receipts/')) {
      cleanId = cleanId.startsWith('receipts/')
        ? `${CLOUDINARY_CONFIG.rootFolder}/${cleanId}`
        : `${CLOUDINARY_FOLDERS.RECEIPTS}/${cleanId}`;
    } else if (cleanId.startsWith('profile-') || cleanId.startsWith('members/')) {
      cleanId = cleanId.startsWith('members/')
        ? `${CLOUDINARY_CONFIG.rootFolder}/${cleanId}`
        : `${CLOUDINARY_FOLDERS.MEMBERS}/${cleanId}`;
    } else if (cleanId.startsWith('doc-') || cleanId.startsWith('documents/')) {
      cleanId = cleanId.startsWith('documents/')
        ? `${CLOUDINARY_CONFIG.rootFolder}/${cleanId}`
        : `${CLOUDINARY_FOLDERS.DOCUMENTS}/${cleanId}`;
    } else {
      cleanId = `${CLOUDINARY_FOLDERS.RECEIPTS}/${cleanId}`;
    }
  }

  // Construct valid CDN URL delivering high-resolution raster JPG
  return `https://res.cloudinary.com/${CLOUDINARY_CONFIG.cloudName}/image/upload/${cleanId}.jpg`;
}

/**
 * Specialized helper: Upload payment receipt / infaq slip
 * Stored in: asmm/receipts/
 * Uses resourceType 'image' so Cloudinary accepts both PDFs and images and enables raster thumbnails,
 * and automatically returns resolved, working HTTPS display URLs without 401/404 errors.
 */
export async function uploadReceiptToCloudinary(
  file: File,
  receiptRef?: string
): Promise<{ secureUrl: string; displayUrl: string; publicId: string }> {
  const cleanRef = (receiptRef || 'receipt').replace(/[^a-zA-Z0-9_-]/g, '');
  const publicId = `receipt-${cleanRef}-${Date.now()}`.slice(0, 80);

  const result = await uploadToCloudinary(file, {
    folder: CLOUDINARY_FOLDERS.RECEIPTS,
    publicId,
    resourceType: 'image'
  });

  const displayUrl = resolveCloudinaryUrl(result.secure_url || result.public_id);

  return {
    secureUrl: displayUrl,
    displayUrl,
    publicId: result.public_id
  };
}

/**
 * Specialized helper: Upload document or PDF
 * Stored in: asmm/documents/
 */
export async function uploadDocumentToCloudinary(
  file: File,
  docTitle?: string
): Promise<{ secureUrl: string; publicId: string }> {
  const cleanTitle = (docTitle || 'doc').trim().toLowerCase().replace(/\s+/g, '-').replace(/[^a-zA-Z0-9_-]/g, '');
  const publicId = `doc-${cleanTitle}-${Date.now()}`.slice(0, 80);

  const result = await uploadToCloudinary(file, {
    folder: CLOUDINARY_FOLDERS.DOCUMENTS,
    publicId,
    resourceType: 'auto'
  });

  return {
    secureUrl: result.secure_url,
    publicId: result.public_id
  };
}
